import { useEffect, useState } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_ID = "contact-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/db74euf799gd4v2febp0";

const SERVICES = [
  {
    icon: "ri-newspaper-line",
    title: "影视资讯",
    text: "追踪最新上线的电影、电视剧与网剧，第一时间整理开播、口碑与话题动态。",
  },
  {
    icon: "ri-book-open-line",
    title: "剧情介绍",
    text: "用通俗的语言梳理每部作品的剧情脉络与关键看点，帮你快速判断是否值得一追。",
  },
  {
    icon: "ri-user-star-line",
    title: "演员资料",
    text: "整理演员简介与完整参演作品清单，支持从演员出发发现更多好剧。",
  },
];

const TIPS = [
  "《云上归途》讲的是怎样的一个故事？",
  "最近有哪些口碑不错的悬疑剧推荐？",
  "想了解某位演员的完整作品列表",
];

export default function About() {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "关于我们 - 隔壁的女孩";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "了解「隔壁的女孩」影视内容网站的定位与内容方向：聚焦最新影视剧资讯、剧情介绍与演员信息，并提供联系与反馈渠道。",
      );
    }
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setSubmitting(true);
    setStatus("idle");
    setFormError("");

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "website_alt") return;
        const text = String(value).trim();
        if (text) params.append(key, text);
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText || "提交失败，请稍后再试";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg);
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后再试。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-100">
      <SiteHeader />
      <main className="pt-16 md:pt-[72px]">
        <div className="border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
              关于「隔壁的女孩」
            </h1>
            <p className="mt-3 max-w-2xl text-sm md:text-base text-foreground-600">
              一个专注于影视资讯、剧情介绍与演员信息的现代影视内容站，写给每一位认真追剧的你。
            </p>
          </div>
        </div>

        <section id="position" className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 scroll-mt-24">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                网站定位
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                让了解一部剧，变得简单
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                「隔壁的女孩」聚焦最新影视剧资讯与剧情深度介绍，面向喜欢追剧、关注影视新作的年轻观众。
                我们不追求堆砌信息，而是希望用清晰的结构与真诚的讲述，帮你快速了解一部作品讲了什么、
                有哪些演员、值不值得看。
              </p>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                在这里，你可以浏览最新上线的影视内容，按类型、地区与年份筛选，查看完整的剧情介绍，
                也能顺着演员资料，发现更多彼此关联的作品。
              </p>
            </div>
            <div className="w-full aspect-[16/11] overflow-hidden rounded-lg border border-background-200 bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=couple%20relaxing%20on%20sofa%20watching%20movie%20on%20large%20tv%20in%20cozy%20evening%20living%20room%2C%20warm%20amber%20lamplight%20with%20deep%20navy%20interior%2C%20cinematic%20editorial%20lifestyle%20photography%2C%20inviting%20atmosphere%2C%20no%20text&width=1200&height=820&seq=about-position&orientation=landscape"
                alt="观众在家追剧的场景"
                title="隔壁的女孩 影视内容站定位"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <section className="bg-background-200/60">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">我们提供什么</h2>
            <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
              {SERVICES.map((item) => (
                <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
                  <span className="flex items-center justify-center w-12 h-12 rounded-md bg-accent-100 text-accent-600 mb-4">
                    <i className={`${item.icon} text-2xl`}></i>
                  </span>
                  <h3 className="font-heading text-lg font-semibold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16 scroll-mt-24">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-500" />
                联系与反馈
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                有想法，随时告诉我们
              </h2>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-foreground-600">
                如果你发现内容有误、希望补充某部作品的资料，或只是想聊聊最近在追的剧，
                都可以通过下面的方式联系我们。我们会认真阅读每一条反馈。
              </p>

              <div className="mt-6 space-y-3">
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex items-center justify-center w-10 h-10 rounded-md bg-secondary-100 text-secondary-900">
                    <i className="ri-mail-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">联系邮箱</p>
                    <a href="mailto:hello@gebi-girl.com" className="text-sm font-medium text-foreground-900 hover:text-accent-600">
                      hello@gebi-girl.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex items-center justify-center w-10 h-10 rounded-md bg-secondary-100 text-secondary-900">
                    <i className="ri-time-line text-lg"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">反馈处理时间</p>
                    <p className="text-sm font-medium text-foreground-900">工作日 1-2 天内回复</p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-sm font-medium text-foreground-800 mb-3">你还可以这样找内容</p>
                <ul className="space-y-2">
                  {TIPS.map((tip) => (
                    <li key={tip} className="flex items-start gap-2 text-sm text-foreground-600">
                      <i className="ri-lightbulb-line mt-0.5 text-accent-600"></i>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-5">意见反馈</h3>
              <form id={FORM_ID} data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">
                    称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复的邮箱"
                    className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400"
                  />
                </div>
                <div>
                  <label htmlFor="contact-topic" className="block text-sm text-foreground-700 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="内容纠错"
                    className="w-full h-11 px-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none focus:border-accent-400 cursor-pointer"
                  >
                    <option value="内容纠错">内容纠错</option>
                    <option value="资料补充">资料补充</option>
                    <option value="合作洽谈">合作洽谈</option>
                    <option value="其他建议">其他建议</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述你的问题或建议（最多 500 字）"
                    className="w-full px-4 py-3 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 outline-none placeholder:text-foreground-400 focus:border-accent-400 resize-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="field-verification-trap"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full inline-flex items-center justify-center gap-2 h-11 rounded-md bg-accent-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className={submitting ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}></i>
                  {submitting ? "提交中..." : "提交反馈"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 rounded-md bg-primary-50 p-3 text-sm text-primary-800">
                    <i className="ri-checkbox-circle-line text-primary-600"></i>
                    感谢你的反馈，我们已经收到啦！
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 rounded-md bg-accent-100 p-3 text-sm text-accent-900">
                    <i className="ri-error-warning-line text-accent-600"></i>
                    {formError || "提交失败，请稍后再试。"}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>

        <section id="copyright" className="bg-background-200/60 scroll-mt-24">
          <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">版权声明</h2>
            <div className="max-w-3xl space-y-4 text-sm md:text-base leading-relaxed text-foreground-600">
              <p>
                本站所有影视剧、演员及相关文字介绍均为演示用途的虚构或整理内容，仅用于展示网站功能，
                不代表任何真实作品的官方信息。相关图片为 AI 生成的示意图，不涉及真实影视作品版权。
              </p>
              <p>
                如需转载本站内容，请注明出处并保留原文链接。若本站内容无意中侵犯了你的权益，
                欢迎通过上方邮箱与我们联系，我们会尽快处理。
              </p>
              <p>© 2024 隔壁的女孩 · 保留所有权利。</p>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}